/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Consolidated Drug Operations hub for Lab, Logistics, and Street Distribution.
 */

import React, { useState, useEffect } from "react";
import {
  Truck,
  FlaskConical,
  MapPin,
  Zap,
  ShieldCheck,
  Package,
  ChevronRight,
  Play,
  Filter,
  CheckSquare,
  Square,
  RotateCcw,
  Activity,
  AlertTriangle,
  Flame,
} from "lucide-react";
import { callBackend } from "../utils/api";
import Cookbook from "./Cookbook";
import { log } from "../utils/logger";

const RARITY_COLORS = {
  common: "text-zinc-400 border-zinc-700",
  rare: "text-blue-400 border-blue-700",
  epic: "text-purple-400 border-purple-700",
  legendary:
    "text-yellow-400 border-yellow-700 shadow-[0_0_10px_rgba(234,179,8,0.15)]",
};

export default function DrugOperations({
  stats,
  inventory,
  recipes,
  stash,
  allianceTag,
  onAssignRunner,
  onAssignCook,
  onSell,
  onUnlock,
  onCook,
}) {
  // Navigation & Data State
  const [activeTab, setActiveTab] = useState("cookbook"); // cookbook, lab, distribution, logistics
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedRecipeId, setSelectedRecipeId] = useState("");
  const [sellAmount, setSellAmount] = useState(10);

  // Staff Management State
  const [selectedIds, setSelectedIds] = useState([]);
  const [rarityFilter, setRarityFilter] = useState("all");
  const [bulkRecipeId, setBulkRecipeId] = useState("");

  // Initial Data Fetch
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const data = await callBackend("/districts/status");
        setDistricts(data);
      } catch (err) {
        log("DRUG_OPERATIONS", "Failed to fetch district intel", err);
      }
    };
    fetchDistricts();
  }, []);

  // Filter & Staff Logic
  const runners = inventory.filter((i) => i.type === "runner");
  const cooks = inventory.filter((i) => i.type === "cook");
  const currentStaff = activeTab === "logistics" ? runners : cooks;

  const filteredStaff = currentStaff.filter(
    (s) => rarityFilter === "all" || s.rarity === rarityFilter,
  );

  // Alliance Bonus Calculations
  const isFriendlyDistrict =
    selectedDistrict?.alliance_tag === allianceTag && allianceTag !== null;
  const heatReduction = isFriendlyDistrict ? 25 : 0;
  const cashBonus = isFriendlyDistrict ? 15 : 0;

  // Handlers
  const handleBulkAssign = () => {
    if (!bulkRecipeId) return;
    selectedIds.forEach((id) => {
      if (activeTab === "logistics") onAssignRunner(id, bulkRecipeId);
      else onAssignCook(id, bulkRecipeId);
    });
    setSelectedIds([]);
    setBulkRecipeId("");
  };

  const handleResetDept = () => {
    const confirmReset = window.confirm(`Reset all ${activeTab} to IDLE?`);
    if (!confirmReset) return;
    currentStaff.forEach((s) => {
      if (activeTab === "logistics") onAssignRunner(s.id, null);
      else onAssignCook(s.id, null);
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  return (
    <div className="space-y-6 font-mono animate-in fade-in duration-300">
      {/* 1. PRIMARY NAVIGATION */}
      <div className="flex gap-2 border-b-2 border-zinc-900 pb-4 overflow-x-auto scrollbar-hide">
        {[
          {
            id: "cookbook",
            label: "COOKBOOK",
            icon: <Flame size={14} />,
          },
          {
            id: "distribution",
            label: "DROPS",
            icon: <MapPin size={14} />,
          },
          {
            id: "lab",
            label: "LAB PRODUCTION",
            icon: <FlaskConical size={14} />,
          },
          {
            id: "logistics",
            label: "LOGISTICS FLEET",
            icon: <Truck size={14} />,
          },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSelectedIds([]);
            }}
            className={`px-6 py-3 text-[10px] font-black italic transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-blue-600/10 text-blue-500 border-blue-500"
                : "bg-zinc-950 text-zinc-600 border-transparent hover:text-zinc-400"
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* BANDO COOKBOOK TAB (MANUAL PRODUCTION) */}
      {activeTab === "cookbook" && (
        <Cookbook
          userStats={stats}
          recipes={recipes}
          onUnlock={onUnlock}
          onCook={onCook}
        />
      )}

      {/* DISTRIBUTION TAB (MANUAL DROPS) */}
      {activeTab === "distribution" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* MANUAL DROP CONTROLS */}
          <div className="bg-zinc-950 border-2 border-blue-900 p-6 space-y-4 shadow-2xl">
            <h3 className="text-xl font-black text-white italic uppercase flex items-center gap-2">
              <Package className="text-blue-500" /> Confirm Manual Drop
            </h3>

            <div className="space-y-4">
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-bold mb-1">
                  Product Selection
                </p>
                <select
                  value={selectedRecipeId}
                  onChange={(e) => setSelectedRecipeId(e.target.value)}
                  className="w-full bg-black border border-zinc-800 p-3 text-blue-400 font-bold outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="">-- SELECT PRODUCT --</option>
                  {stash?.map((p) => (
                    <option key={p.recipe_id} value={p.recipe_id}>
                      {p.name.toUpperCase()} ({p.quantity} UNITS)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-bold mb-1">
                  Target Sector
                </p>
                <select
                  onChange={(e) =>
                    setSelectedDistrict(
                      districts.find((d) => d.id === parseInt(e.target.value)),
                    )
                  }
                  className="w-full bg-black border border-zinc-800 p-3 text-white font-bold outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="">-- SELECT TERRITORY --</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-[10px] font-black">
                    QTY
                  </span>
                  <input
                    type="number"
                    value={sellAmount}
                    onChange={(e) =>
                      setSellAmount(parseInt(e.target.value) || 0)
                    }
                    className="w-full bg-black border border-zinc-800 p-3 pl-12 text-white font-mono outline-none focus:border-blue-500"
                  />
                </div>
                <button
                  onClick={() =>
                    onSell(selectedRecipeId, sellAmount, selectedDistrict?.id)
                  }
                  disabled={
                    !selectedRecipeId || !selectedDistrict || sellAmount <= 0
                  }
                  className="bg-blue-600 hover:bg-blue-400 disabled:bg-zinc-900 disabled:text-zinc-700 text-black font-black px-8 uppercase italic transition-all flex items-center gap-2"
                >
                  <Zap size={16} fill="currentColor" /> Sell Weight
                </button>
              </div>
            </div>
          </div>

          {/* TERRITORY INTEL */}
          <div className="bg-zinc-900 border border-zinc-800 p-6 flex flex-col justify-between">
            <div>
              <h4 className="text-xs font-black text-zinc-400 uppercase mb-4 flex items-center gap-2">
                <Activity size={14} className="text-blue-500" /> Sector Analysis
              </h4>

              {selectedDistrict ? (
                <div className="space-y-4 animate-in fade-in slide-in-from-right duration-500">
                  <div className="flex justify-between items-center border-b border-zinc-800 pb-2">
                    <span className="text-lg font-black text-white italic">
                      {selectedDistrict.name}
                    </span>
                    <span className="text-[10px] px-2 py-1 bg-black text-zinc-500 border border-zinc-800 font-bold">
                      TAG: {selectedDistrict.alliance_tag || "NEUTRAL"}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`p-4 border transition-colors ${isFriendlyDistrict ? "border-green-500 bg-green-900/10" : "border-zinc-800 bg-black/40"}`}
                    >
                      <p className="text-[10px] text-zinc-500 uppercase font-black">
                        District Bonus
                      </p>
                      <p
                        className={`text-xl font-black ${isFriendlyDistrict ? "text-green-500" : "text-zinc-700"}`}
                      >
                        {isFriendlyDistrict ? `+${cashBonus}%` : "0%"}
                      </p>
                    </div>
                    <div className="p-4 border border-zinc-800 bg-black/40">
                      <p className="text-[10px] text-zinc-500 uppercase font-black">
                        Risk Mitigation
                      </p>
                      <p
                        className={`text-xl font-black ${isFriendlyDistrict ? "text-blue-400" : "text-red-500"}`}
                      >
                        {isFriendlyDistrict
                          ? `-${heatReduction}% Heat`
                          : "STD RISK"}
                      </p>
                    </div>
                  </div>

                  {isFriendlyDistrict && (
                    <div className="p-3 border border-green-900/50 bg-green-950/20 rounded flex items-start gap-3">
                      <ShieldCheck
                        size={20}
                        className="text-green-500 shrink-0"
                      />
                      <p className="text-[10px] text-green-400 leading-tight">
                        <span className="font-black">SYNDICATE ADVANTAGE:</span>{" "}
                        Selling in your alliance's sector provides a cash bonus
                        and significantly reduces the chance of police busts.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-40 flex flex-col items-center justify-center border-2 border-dashed border-zinc-800 text-zinc-700">
                  <MapPin size={32} className="mb-2 opacity-20" />
                  <p className="text-[10px] uppercase font-black italic">
                    Initialize satellite uplink for territory data
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center gap-2 text-zinc-600 bg-black/40 p-3 border border-zinc-800">
              <AlertTriangle size={14} className="text-yellow-600 shrink-0" />
              <p className="text-[9px] uppercase font-bold tracking-tighter leading-none">
                Risk Warning: Moving weight without alliance control increases
                police heat by 45%.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* LAB & LOGISTICS CONTENT (AUTO PRODUCTION & DISTRIBUTION) */}
      {(activeTab === "lab" || activeTab === "logistics") && (
        <div className="space-y-6 animate-in slide-in-from-bottom duration-500">
          {/* Analytics Dash */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`p-4 border border-l-4 ${activeTab === "lab" ? "border-purple-600 bg-purple-900/10" : "border-blue-600 bg-blue-900/10"}`}
            >
              <p className="text-[10px] text-zinc-500 uppercase font-black">
                Active Personnel
              </p>
              <p className="text-3xl font-black text-white italic">
                {currentStaff.reduce((s, u) => s + u.quantity, 0)} Units
              </p>
            </div>
            <div
              className="p-4 border border-zinc-800 bg-black flex justify-between items-center group cursor-pointer hover:border-red-600 transition-all"
              onClick={handleResetDept}
            >
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-black">
                  Operational Status
                </p>
                <p className="text-xl font-black text-white italic group-hover:text-red-500 transition-colors">
                  Emergency Reset
                </p>
              </div>
              <RotateCcw
                size={24}
                className="text-zinc-800 group-hover:text-red-600 group-hover:rotate-[-90deg] transition-all"
              />
            </div>
          </div>

          {/* Filtering & Bulk Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 bg-zinc-950 p-4 border border-zinc-800 shadow-xl">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1.5">
                <Filter size={14} className="text-zinc-500" />
                <select
                  value={rarityFilter}
                  onChange={(e) => setRarityFilter(e.target.value)}
                  className="bg-transparent text-[10px] text-white outline-none uppercase font-bold cursor-pointer"
                >
                  <option value="all">All Tiers</option>
                  <option value="common">Common</option>
                  <option value="rare">Rare</option>
                  <option value="epic">Epic</option>
                  <option value="legendary">Legendary</option>
                </select>
              </div>
              <button
                onClick={() =>
                  setSelectedIds(
                    selectedIds.length === filteredStaff.length
                      ? []
                      : filteredStaff.map((s) => s.id),
                  )
                }
                className="text-[10px] font-black text-zinc-500 hover:text-white uppercase transition-colors"
              >
                {selectedIds.length === filteredStaff.length
                  ? "Deselect All"
                  : "Select All Visible"}
              </button>
            </div>
          </div>

          {/* Bulk Action Sticky Bar */}
          {selectedIds.length > 0 && (
            <div className="sticky top-4 z-[100] bg-blue-600 p-4 border-2 border-zinc-900 shadow-[0_10px_40px_rgba(0,0,0,0.5)] flex items-center justify-between animate-in slide-in-from-top duration-300">
              <div className="text-black">
                <p className="text-[9px] font-black uppercase italic leading-none">
                  Mass Assignment
                </p>
                <p className="text-lg font-black leading-none">
                  {selectedIds.length} Units Selected
                </p>
              </div>
              <div className="flex gap-2">
                <select
                  value={bulkRecipeId}
                  onChange={(e) => setBulkRecipeId(e.target.value)}
                  className="bg-black text-white text-xs p-2 border-none outline-none min-w-[180px] font-bold"
                >
                  <option value="">-- SELECT PRODUCT --</option>
                  {recipes
                    .filter((r) => r.unlocked)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name.toUpperCase()}
                      </option>
                    ))}
                </select>
                <button
                  onClick={handleBulkAssign}
                  disabled={!bulkRecipeId}
                  className="bg-zinc-100 text-black px-6 py-2 font-black uppercase text-xs hover:bg-white disabled:bg-zinc-300 transition-all flex items-center gap-2"
                >
                  <Play size={14} fill="currentColor" /> Dispatch Units
                </button>
              </div>
            </div>
          )}

          {/* Staff Unit Grid */}
          <div className="grid grid-cols-1 gap-2">
            {filteredStaff.length === 0 ? (
              <div className="p-20 text-center border-2 border-dashed border-zinc-800 text-zinc-700 uppercase font-black italic">
                No personnel found matching these filters
              </div>
            ) : (
              filteredStaff.map((unit) => {
                const isSelected = selectedIds.includes(unit.id);
                const currentRecipe = recipes.find(
                  (r) => r.id === unit.assigned_recipe_id,
                );

                return (
                  <div
                    key={unit.id}
                    onClick={() => toggleSelect(unit.id)}
                    className={`group flex items-center justify-between p-4 border cursor-pointer transition-all ${
                      isSelected
                        ? "bg-zinc-800 border-white"
                        : "bg-zinc-950 border-zinc-900 hover:border-zinc-700"
                    } border-l-4 ${RARITY_COLORS[unit.rarity] || RARITY_COLORS.common}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`transition-colors ${isSelected ? "text-blue-500" : "text-zinc-800"}`}
                      >
                        {isSelected ? (
                          <CheckSquare size={20} />
                        ) : (
                          <Square size={20} />
                        )}
                      </div>
                      <div>
                        <h4 className="font-black text-white uppercase italic text-sm flex items-center gap-2">
                          {unit.name}
                          {unit.assigned_recipe_id && (
                            <span
                              className={`text-[8px] px-1.5 py-0.5 font-bold border rounded-full ${
                                activeTab === "logistics"
                                  ? "bg-blue-900 text-blue-300 border-blue-800"
                                  : "bg-purple-900 text-purple-300 border-purple-800"
                              }`}
                            >
                              BUSY
                            </span>
                          )}
                        </h4>
                        <p className="text-[10px] text-zinc-600 uppercase font-bold mt-1">
                          QTY: {unit.quantity} |{" "}
                          {activeTab === "logistics" ? "CAP" : "RATE"}:{" "}
                          {activeTab === "logistics"
                            ? Math.round(unit.production_rate * 100) + "%"
                            : unit.production_rate + "u/tick"}
                        </p>
                      </div>
                    </div>

                    <div className="text-right flex items-center gap-6">
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase font-black">
                          Project
                        </p>
                        <p
                          className={`text-xs font-black italic ${unit.assigned_recipe_id ? "text-white" : "text-zinc-700"}`}
                        >
                          {currentRecipe
                            ? currentRecipe.name.toUpperCase()
                            : "IDLE"}
                        </p>
                      </div>
                      <ChevronRight
                        size={18}
                        className="text-zinc-800 group-hover:text-zinc-500 transition-colors"
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
